import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { sortHeights } from '@/lib/heights';
import { FIT_OPTIONS } from '@/lib/fit';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Familiar hints for the default clothing types; other (shoe) types show label only.
const EDIT_TYPE_HINTS = {
  Upper: "Shirt, T-shirt, Hoodie",
  Lower: "Jeans, Pants, Shorts",
};

const ProductEditForm = ({ open, onClose, onSuccess, product }) => {
  useBodyScrollLock(open);
  const [formData, setFormData] = useState({
    product_name: "",
    product_link: "",
    category: "",
    heights: [],
    category_id: "",
    fit: "",
    brand_id: "",
    price: ""
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API}/categories`, { withCredentials: true })
      .then((res) => setCategories(res.data || []))
      .catch(() => setCategories([]));
    axios
      .get(`${API}/brands`, { withCredentials: true })
      .then((res) => setBrands(res.data || []))
      .catch(() => setBrands([]));
  }, [open]);

  // Heights available depend on the chosen Category Tab.
  const DEFAULT_HEIGHTS = ['6.0', '6.1', '6.2', '6.3', '6.4', '6.5'];
  const selectedCat = categories.find((c) => c.id === formData.category_id);
  const editFormHeights = sortHeights(
    selectedCat?.heights?.length ? selectedCat.heights : DEFAULT_HEIGHTS
  );
  // Type options + size unit follow the chosen tab (clothing vs shoes).
  const formIsShoes = selectedCat?.kind === "shoes";
  const formSizeLabel = (selectedCat?.size_label || "ft").trim() || "ft";
  const formTypes = selectedCat?.types?.length ? selectedCat.types : ["Upper", "Lower"];

  // Drop selected heights that don't apply when category changes. Only prune
  // once the category is actually resolved — otherwise it would strip valid
  // heights against the default (men's) set before categories have loaded.
  useEffect(() => {
    if (!formData.category_id || categories.length === 0) return;
    const cat = categories.find((c) => c.id === formData.category_id);
    if (!cat) return;
    const allowed = new Set(sortHeights(cat.heights?.length ? cat.heights : DEFAULT_HEIGHTS));
    const pruned = formData.heights.filter((h) => allowed.has(h));
    if (pruned.length !== formData.heights.length) {
      setFormData((prev) => ({ ...prev, heights: pruned }));
    }
    // Clear a stale type that doesn't belong to this tab's type list.
    const catTypes = cat.types?.length ? cat.types : ["Upper", "Lower"];
    if (formData.category && !catTypes.includes(formData.category)) {
      setFormData((prev) => ({ ...prev, category: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.category_id, categories]);

  // Pre-fill form with existing product data
  useEffect(() => {
    if (product) {
      setFormData({
        product_name: product.product_name || "",
        product_link: product.product_link || "",
        category: product.category || "",
        heights: product.heights || [],
        category_id: product.category_id || "",
        fit: product.fit_feedback || "",
        brand_id: product.brand_id || "",
        price: product.price || ""
      });
      // Set existing image preview
      if (product.image_url) {
        setImagePreview(`${BACKEND_URL}${product.image_url}`);
      }
    }
  }, [product]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleHeightToggle = (height) => {
    const currentHeights = formData.heights;
    if (currentHeights.includes(height)) {
      setFormData({ ...formData, heights: currentHeights.filter(h => h !== height) });
    } else {
      setFormData({ ...formData, heights: [...currentHeights, height] });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.product_name || !formData.product_link || !formData.category || formData.heights.length === 0) {
      toast.error("Please fill all fields and select at least one height");
      return;
    }

    setLoading(true);
    
    try {
      const submitData = new FormData();
      submitData.append("product_name", formData.product_name);
      submitData.append("product_link", formData.product_link);
      submitData.append("category", formData.category);
      submitData.append("heights", formData.heights.join(","));
      submitData.append("category_id", formData.category_id || "");
      submitData.append("fit_feedback", formData.fit || "");
      submitData.append("brand_id", formData.brand_id || "");
      submitData.append("price", formData.price.trim());

      // Only append image if user selected a new one
      if (image) {
        submitData.append("image", image);
      }

      await axios.put(`${API}/products/${product.id}`, submitData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true
      });

      toast.success("Product updated successfully!");
      onSuccess();
    } catch (error) {
      console.error("Error updating product:", error);
      if (error.response?.status === 403) {
        toast.error("You don't have permission to edit products");
      } else {
        toast.error("Failed to update product. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm p-4 flex items-center justify-center"
      data-testid="product-edit-overlay"
    >
      <div
        className="bg-white w-full max-w-lg rounded-md border border-[#E4E1D9] p-6 md:p-8 max-h-[90vh] overflow-y-auto"
        data-lenis-prevent
        data-testid="product-edit-dialog"
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <h2 
            className="text-3xl tracking-tighter" 
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="edit-form-title"
          >
            Edit Product
          </h2>
          <button
            onClick={onClose}
            className="text-[#6B6A68] hover:text-[#1A1A1A] transition-colors"
            data-testid="edit-form-close-button"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Product Name */}
          <div>
            <label 
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              Product Name
            </label>
            <input
              type="text"
              name="product_name"
              value={formData.product_name}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
              placeholder="e.g., Levi's 511 Slim Fit Jeans"
              data-testid="edit-product-name-input"
            />
          </div>

          {/* Product Link */}
          <div>
            <label 
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              Product Link
            </label>
            <input
              type="url"
              name="product_link"
              value={formData.product_link}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
              placeholder="https://..."
              data-testid="edit-product-link-input"
            />
          </div>

          {/* Category Tab (admin-managed) */}
          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="edit-category-id-label"
            >
              Category Tab
            </label>
            <select
              name="category_id"
              value={formData.category_id}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="edit-category-id-select"
            >
              <option value="">All (no specific tab)</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Category */}
          <div>
            <label 
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              Category
            </label>
            <div className="grid grid-cols-2 gap-3">
              {formTypes.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setFormData({ ...formData, category: t })}
                  className={`px-4 py-3 border rounded-md transition-all ${
                    formData.category === t
                      ? "bg-[#2D302A] text-white border-[#2D302A]"
                      : "bg-white text-[#1A1A1A] border-[#E4E1D9] hover:border-[#2D302A]"
                  }`}
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  data-testid={`edit-category-type-${t.toLowerCase().replace(/\s+/g, "-")}-button`}
                >
                  <span className="text-xs uppercase tracking-[0.2em]">{t}</span>
                  {EDIT_TYPE_HINTS[t] && (
                    <div className="text-xs text-current/70 mt-1 normal-case tracking-normal">{EDIT_TYPE_HINTS[t]}</div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Height - Multiple Selection */}
          <div>
            <label 
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              {formIsShoes ? "Size Fit (Select all that apply)" : "Height Fit (Select all that apply)"}
            </label>
            <div className="grid grid-cols-2 gap-3">
              {editFormHeights.map((height) => (
                <button
                  key={height}
                  type="button"
                  onClick={() => handleHeightToggle(height)}
                  className={`px-4 py-3 border rounded-md transition-all text-center ${
                    formData.heights.includes(height)
                      ? "bg-[#2D302A] text-white border-[#2D302A]"
                      : "bg-white text-[#1A1A1A] border-[#E4E1D9] hover:border-[#2D302A]"
                  }`}
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  data-testid={`edit-height-${height}-button`}
                >
                  <span className="text-sm uppercase tracking-[0.2em]">{formIsShoes ? `${formSizeLabel} ${height}` : `${height} ${formSizeLabel}`}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Fit feedback (optional) */}
          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              How did it fit? (optional)
            </label>
            <div className="space-y-2">
              {FIT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, fit: formData.fit === opt.value ? "" : opt.value })
                  }
                  className={`w-full text-left px-4 py-3 border rounded-md transition-all ${
                    formData.fit === opt.value
                      ? "bg-[#2D302A] text-white border-[#2D302A]"
                      : "bg-white text-[#1A1A1A] border-[#E4E1D9] hover:border-[#2D302A]"
                  }`}
                  style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  data-testid={`edit-fit-${opt.value}-button`}
                >
                  <span className="text-sm">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Brand (optional) */}
          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              Brand (optional)
            </label>
            <select
              value={formData.brand_id}
              onChange={(e) => setFormData({ ...formData, brand_id: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
              data-testid="edit-brand-select"
            >
              <option value="">No brand</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          {/* Price (optional) */}
          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              Price (optional)
            </label>
            <input
              type="text"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
              placeholder="e.g., $49 or ₹2,499"
              maxLength={20}
              data-testid="edit-price-input"
            />
            <p className="text-[10px] text-[#6B6A68] mt-1">
              Shown as a tag on the product. Leave blank to hide it.
            </p>
          </div>

          {/* Image Upload */}
          <div>
            <label 
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              Product Image (Leave unchanged or upload new)
            </label>
            <div 
              className="border-2 border-dashed border-[#E4E1D9] rounded-md p-8 text-center hover:border-[#2D302A] transition-colors cursor-pointer"
              onClick={() => document.getElementById('edit-image-input').click()}
              data-testid="edit-image-upload-area"
            >
              {imagePreview ? (
                <div className="space-y-3">
                  <img src={imagePreview} alt="Preview" className="max-h-48 mx-auto rounded" data-testid="edit-image-preview" />
                  <p className="text-sm text-[#6B6A68]">Click to change image</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <UploadSimple size={32} className="mx-auto text-[#6B6A68]" />
                  <p className="text-sm text-[#6B6A68]">Click to upload product image</p>
                </div>
              )}
            </div>
            <input
              id="edit-image-input"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
              data-testid="edit-image-input"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#2D302A] text-white px-6 py-4 rounded-md hover:bg-[#1A1C18] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            data-testid="update-product-button"
          >
            <span className="text-xs uppercase tracking-[0.2em]">
              {loading ? "Updating..." : "Update Product"}
            </span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProductEditForm;
